import { DeepPartial } from '@apollo/client/utilities'
import {
  InboxTaskListItemCompletionFragment,
  InboxTaskListItemFragment,
  TaskableFragment,
  TaskableInfoForMetadataFragment,
  TaskableInfoForTitleFragment,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import buildUrl from 'modules/core/lib/buildUrl'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { ManualTaskDefinition } from 'modules/inbox_tasks/tasks/manual'

import { PossibleTaskState } from './abstraction'
import { TaskDefinition, getTaskDefinitions } from './definitions'

const getTaskDefinition = ({
  taskableTypename,
}: {
  taskableTypename?: TaskableFragment['__typename']
}) => {
  const definitions = getTaskDefinitions()

  return taskableTypename
    ? definitions.find((task) => task.taskableTypename === taskableTypename)
    : ManualTaskDefinition
}

export const useTaskDefinition = <TaskableTypename extends TaskableFragment['__typename']>({
  taskableTypename,
}: {
  taskableTypename?: TaskableTypename
}) => {
  return getTaskDefinition({ taskableTypename })
}

const isTaskableOfType = <T extends TaskDefinition['taskableTypename']>(
  taskable: TaskableFragment,
  typename: T
): taskable is Extract<TaskableFragment, { __typename: T }> => {
  return taskable?.__typename === typename
}

const isTitleTaskableOfType = <T extends TaskDefinition['taskableTypename']>(
  taskable: DeepPartial<TaskableInfoForTitleFragment>,
  typename: T
): taskable is Extract<
  DeepPartial<TaskableInfoForTitleFragment & { __typename: T }>,
  { __typename: T }
> => {
  return taskable?.__typename === typename
}

const isMetadataTaskableOfType = <T extends TaskDefinition['taskableTypename']>(
  taskable: TaskableInfoForMetadataFragment,
  typename: T
): taskable is Extract<TaskableInfoForMetadataFragment, { __typename: T }> => {
  return taskable?.__typename === typename
}

export const getTaskableForTypename = <TaskableTypename extends TaskDefinition['taskableTypename']>(
  taskable: TaskableFragment,
  taskDefinition: TaskDefinition
): Extract<TaskableFragment, { __typename: TaskableTypename }> => {
  if (!isTaskableOfType(taskable, taskDefinition.taskableTypename)) {
    throw new Error(
      `Taskable ${taskable?.__typename} is not of type ${taskDefinition.taskableTypename}`
    )
  }

  return taskable
}

const getTitleTaskableForTypename = <
  TaskableTypename extends TaskableInfoForTitleFragment['__typename'],
>(
  taskableInfo: DeepPartial<TaskableInfoForTitleFragment>,
  taskDefinition: TaskDefinition
): Extract<TaskableFragment, { __typename: TaskableTypename }> => {
  if (!isTitleTaskableOfType(taskableInfo, taskDefinition.taskableTypename)) {
    throw new Error(
      `Taskable ${taskableInfo?.__typename} is not of type ${taskDefinition.taskableTypename}`
    )
  }

  return taskableInfo
}

const getMetadataTaskableForTypename = <
  TaskableTypename extends TaskableInfoForMetadataFragment['__typename'],
>(
  taskableInfo: TaskableInfoForMetadataFragment,
  taskDefinition: TaskDefinition
): Extract<TaskableFragment, { __typename: TaskableTypename }> => {
  if (!isMetadataTaskableOfType(taskableInfo, taskDefinition.taskableTypename)) {
    throw new Error(
      `Taskable ${taskableInfo?.__typename} is not of type ${taskDefinition.taskableTypename}`
    )
  }

  return taskableInfo
}

export const getStateForTaskDefinition = <TaskState extends TaskDefinition['initialState']>(
  initialState?: PossibleTaskState,
  taskInitialState?: TaskState
): initialState is TaskState => {
  return JSON.stringify(initialState) === JSON.stringify(taskInitialState)
}

export const getTaskTitle = <TaskableTypename extends TaskableInfoForTitleFragment['__typename']>({
  taskName,
  taskableInfoForTitle,
  affecteeName,
  groupedCount,
  shortVersion,
  dueOn,
}: {
  taskName: string
  taskableInfoForTitle?: DeepPartial<
    TaskableInfoForTitleFragment & { __typename: TaskableTypename }
  > | null
  affecteeName: string
  groupedCount?: number
  shortVersion?: boolean
  dueOn?: string
}) => {
  if (!taskableInfoForTitle) return taskName

  const taskDefinition = getTaskDefinition({
    taskableTypename: taskableInfoForTitle.__typename,
  })

  const safeTaskable = taskDefinition
    ? getTitleTaskableForTypename(taskableInfoForTitle, taskDefinition)
    : undefined

  if (!taskDefinition?.getTitle || !safeTaskable) {
    return taskName
  }

  return taskDefinition.getTitle({
    info: safeTaskable,
    affecteeName,
    groupedCount,
    shortVersion,
    dueOn,
  })
}

export const getTaskUrl = ({
  taskableInfoForUrl,
  resourceId,
  navigation,
  groupedCount,
  status,
}: {
  taskableInfoForUrl?: TaskableInfoForMetadataFragment | null
  resourceId?: number
  navigation: Navigation<NavigationPaths>
  groupedCount?: number
  status?: TasksValueObjectsTaskStatusEnum
}) => {
  const taskDefinition = getTaskDefinition({
    taskableTypename: taskableInfoForUrl?.__typename,
  })

  const safeTaskable =
    taskDefinition && taskableInfoForUrl
      ? getMetadataTaskableForTypename(taskableInfoForUrl, taskDefinition)
      : undefined

  if (
    !taskDefinition?.getUrl ||
    taskDefinition.taskableTypename !== taskableInfoForUrl?.__typename ||
    !safeTaskable
  ) {
    return
  }

  return taskDefinition.getUrl({ info: safeTaskable, resourceId, navigation, groupedCount, status })
}

export const getTaskDescription = ({
  taskableInfoForDescription,
}: {
  taskableInfoForDescription?: TaskableInfoForMetadataFragment | null
}) => {
  if (!taskableInfoForDescription) return

  const taskDefinition = getTaskDefinition({
    taskableTypename: taskableInfoForDescription.__typename,
  })

  const safeTaskable = taskDefinition
    ? getMetadataTaskableForTypename(taskableInfoForDescription, taskDefinition)
    : undefined

  if (!safeTaskable) return

  const metadata = taskDefinition?.getMetadata?.({ info: safeTaskable })

  return metadata
}

export const useTaskListItemUrl = () => {
  const { navigation } = useNavigation()

  const getTaskListItemUrl = (
    item: InboxTaskListItemFragment & InboxTaskListItemCompletionFragment
  ) => {
    const url = getTaskUrl({
      taskableInfoForUrl: item.taskable,
      resourceId: item.resourceId ?? undefined,
      navigation,
      groupedCount: item.groupedTasksConnection?.totalCount ?? 0,
      status: item.status ?? undefined,
    })

    if (url) return url

    if (isTaskFlaggedToBeDiscarded(item)) {
      return `${location.pathname}#${encodeActivity('core.noPermissionTask', { id: `${item.id}` })}`
    }

    return `${location.pathname}#${encodeActivity('core.task', { id: `${item.id}` })}`
  }

  return { getTaskListItemUrl }
}

export const isTaskFlaggedToBeDiscarded = (item: DeepPartial<InboxTaskListItemFragment>) => {
  const isManual = !item.kind || item.kind === 'manual'

  if (isManual) return false

  const isEsignatureWithoutDocument =
    item.taskable?.__typename === 'EsignatureSignature' && !item.taskable.document?.id

  if (isEsignatureWithoutDocument) return true

  const isApprovalV2TaskWithoutResource =
    item.taskable?.__typename === 'ApprovalsV2ReviewRequest' &&
    !item.taskable?.approvalProcess?.resource

  if (isApprovalV2TaskWithoutResource) return true

  return !item.resourceId
}

export const getTaskListItemAffecteeInfo = (
  item: InboxTaskListItemFragment | DeepPartial<InboxTaskListItemFragment>
) => {
  const person = item.affecteesConnection?.nodes?.[0]?.employee ?? item.author

  const personFirstName = (person?.preferredName || person?.firstName) ?? ''
  const personLastName = person?.lastName ?? ''

  const avatar = person?.avatar?.url ? buildUrl(person.avatar.url) : undefined

  let avatarFile:
    | {
        filename: string
        extension?: string
      }
    | undefined
  if (item.taskable?.__typename === 'DocumentsDocument') {
    avatarFile = {
      filename: item.taskable?.filename ?? '',
      extension: item.taskable.extension?.slice(1),
    }
  } else if (item.taskable?.__typename === 'EsignatureSignature') {
    avatarFile = {
      filename: item.taskable?.document?.filename ?? '',
      extension: item.taskable?.document?.extension?.slice(1),
    }
  }

  return {
    firstName: personFirstName,
    lastName: personLastName,
    fullName: `${personFirstName} ${personLastName}`,
    avatar,
    avatarFile,
  }
}
